import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';

import { Alert, Box, Button, Paragraph, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyAvailabilityWebApiHandler_confirm } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import PropertiesActions from '../../../../actions/PropertiesActions';
import { useSelector } from '../../../../selectors/useSelector';
import ListingAvailabilityModal from '../../../Modal/ListingAvailabilityModal';

import type { UserPropertyAvailabilityConfirmResponseWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

const ListingAvailability = ({ expirationDateTimestamp }: { expirationDateTimestamp: string | number }) => {
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const listingAlias = useSelector((state) => state.properties.currentProperty.listingAlias);

  const [displayLearnMoreModal, setDisplayLearnMoreModal] = useState(false);

  const { mutate: confirmListingAvailability } = useRentalManagerUserPropertyAvailabilityWebApiHandler_confirm({
    onError: () => {
      if (displayLearnMoreModal) {
        setDisplayLearnMoreModal(false);
      }
      enqueueToast(<Toast tone="critical">An error occurred while confirming your listing availability</Toast>, {
        duration: 10000,
      });
    },
    onSuccess: (response: UserPropertyAvailabilityConfirmResponseWebDto) => {
      const newExpiration = String(response.newExpirationMs);
      dispatch(
        PropertiesActions.updateCurrentProperty({
          expirationDateTimestamp: newExpiration,
        }),
      );
      queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });

      if (displayLearnMoreModal) {
        setDisplayLearnMoreModal(false);
      }

      enqueueToast(
        <Toast tone="success">
          {`Thanks for confirming! We’ll keep your listing active until ${format(
            new Date(response.newExpirationMs),
            'MM/dd/yy',
          )}`}
        </Toast>,
        {
          duration: 10000,
        },
      );
    },
  });

  const handleLearnMoreClick = () => {
    setDisplayLearnMoreModal(true);
  };

  return (
    <Alert tone="warning">
      <Box
        css={{
          display: 'flex',
          flexDirection: { base: 'column', xl: 'row' },
          alignItems: { base: 'flex-start', xl: 'center' },
          justifyContent: { base: 'flex-start', xl: 'space-between' },
        }}
      >
        <Box>
          <Paragraph textStyle="body-bold">Is this listing still available?</Paragraph>
          <Paragraph textStyle="body" css={{ marginBottom: { base: '200', xl: 0 } }}>
            {`Listing expires ${format(new Date(Number(expirationDateTimestamp)), 'MMM dd, yyyy')}`}
          </Paragraph>
        </Box>

        <Box
          css={{
            display: 'flex',
            flexDirection: { base: 'column', xl: 'row' },
            alignItems: { base: 'flex-start', xl: 'center' },
          }}
        >
          <Box css={{ display: 'flex', order: { base: 1, xl: 2 } }}>
            <Button
              css={{ marginBottom: { lgDown: '200' }, marginRight: { lgDown: '400' } }}
              tone="brand"
              emphasis="filled"
              size="sm"
              onClick={() => confirmListingAvailability({ getParams: { listingAlias } })}
            >
              Confirm availability
            </Button>
          </Box>
          <Box css={{ display: 'flex', order: { base: 2, xl: 1 } }}>
            <TextButton onClick={handleLearnMoreClick} textStyle="body-sm-bold" css={{ marginRight: { xl: '400' } }}>
              Learn more
            </TextButton>
          </Box>
        </Box>
      </Box>

      {displayLearnMoreModal && (
        <ListingAvailabilityModal
          onConfirmClick={() => confirmListingAvailability({ getParams: { listingAlias } })}
          onModalClose={() => setDisplayLearnMoreModal(false)}
        />
      )}
    </Alert>
  );
};

export default ListingAvailability;
